// BEGIN FILE: src/astro/karmic/karmicFrictionEngine.ts
// Computes Life Friction Index (LFI) per house from per-planet friction scores.
// Weighting rules:
//   • Resident planets (in house) → highest weight (50% total, split)
//   • House lord → 30% (or 50% when house is empty)
//   • Aspecting planets → 20% (or 50% when house is empty), split among aspectors
//   • When house is EMPTY: lord + aspectors share full weight at higher rates

import { getHouseLord } from '../../utils/lordshipUtils';
import type { PlanetName } from './frictionQuestions';

// Parashari aspect offsets from the source planet's house (0-based offset)
const PARASHARI_OFFSETS: Record<string, number[]> = {
  Sun:     [6],
  Moon:    [6],
  Mercury: [6],
  Venus:   [6],
  Mars:    [3, 6, 7],
  Jupiter: [4, 6, 8],
  Saturn:  [2, 6, 9],
  Rahu:    [4, 6, 8],
  Ketu:    [4, 6, 8],
};

export interface HousePlanetRoles {
  house: number;
  residents: PlanetName[];    // planets sitting IN this house
  lord: PlanetName | null;    // house lord
  aspectors: PlanetName[];    // planets aspecting from another house (excluding lord if also aspecting)
}

export interface HouseLFIResult {
  lfi: number;                      // 0-100 weighted composite
  residentScore: number | null;     // avg friction of residents with data, null if none
  lordScore: number | null;         // friction of lord, null if lord data missing
  aspectorScore: number | null;     // avg friction of aspectors with data, null if none
  connectedPlanets: PlanetName[];   // all planets contributing (for tooltip)
  // Per-role planet lists for tooltip display
  residentPlanets: PlanetName[];
  lordPlanet: PlanetName | null;
  aspectorPlanets: PlanetName[];
  planetScores: Record<string, number>; // planet → friction score (only planets with data)
}

/** Derive resident / lord / aspector roles for a given house */
export function getHousePlanetRoles(
  house: number,
  planetPlacements: Record<string, { house: number }>,
  ascendantSignIndex: number,
): HousePlanetRoles {
  const residents: PlanetName[] = Object.entries(planetPlacements)
    .filter(([, pl]) => pl.house === house)
    .map(([p]) => p as PlanetName);

  let lord: PlanetName | null = null;
  try {
    lord = getHouseLord(house as any, ascendantSignIndex) as PlanetName;
  } catch {
    lord = null;
  }

  // Aspectors: planets in other houses whose Parashari aspect hits this house
  // Exclude the lord if it also aspects (to avoid double-counting in weights)
  const aspectors: PlanetName[] = Object.entries(planetPlacements)
    .filter(([, pl]) => pl.house !== house)
    .filter(([planet, pl]) => {
      const offsets = PARASHARI_OFFSETS[planet] || [6];
      return offsets.some(off => ((pl.house - 1 + off) % 12) + 1 === house);
    })
    .map(([p]) => p as PlanetName)
    .filter(p => p !== lord);  // lord handled separately

  return { house, residents, lord, aspectors };
}

/** Compute weights for each planet contributing to a house's LFI */
function computeWeights(roles: HousePlanetRoles): Record<string, number> {
  const weights: Record<string, number> = {};
  const hasResidents = roles.residents.length > 0;
  const hasLord      = roles.lord !== null;
  const hasAspectors = roles.aspectors.length > 0;

  if (hasResidents) {
    // Residents: 50% total, split evenly
    const resWeight = 0.50 / roles.residents.length;
    roles.residents.forEach(p => { weights[p] = resWeight; });

    if (hasLord) {
      const remaining = 0.50;
      if (hasAspectors) {
        weights[roles.lord!] = 0.30;
        roles.aspectors.forEach(p => { weights[p] = (weights[p] ?? 0) + 0.20 / roles.aspectors.length; });
      } else {
        weights[roles.lord!] = remaining;
      }
    } else if (hasAspectors) {
      roles.aspectors.forEach(p => { weights[p] = (weights[p] ?? 0) + 0.50 / roles.aspectors.length; });
    }
  } else {
    // EMPTY house — lord + aspectors get full weight
    if (hasLord && hasAspectors) {
      weights[roles.lord!] = 0.50;
      roles.aspectors.forEach(p => { weights[p] = (weights[p] ?? 0) + 0.50 / roles.aspectors.length; });
    } else if (hasLord) {
      weights[roles.lord!] = 1.00;
    } else if (hasAspectors) {
      roles.aspectors.forEach(p => { weights[p] = (weights[p] ?? 0) + 1.0 / roles.aspectors.length; });
    }
  }

  return weights;
}

/**
 * Compute Life Friction Index for a single house.
 * Returns lfi 0-100 plus three separate data points for the multi-dot bar.
 */
export function computeHouseLFI(
  roles: HousePlanetRoles,
  frictionScores: Record<string, number>,  // planet -> 0-100
): HouseLFIResult {
  const weights = computeWeights(roles);

  let weightedSum = 0;
  let totalWeight = 0;

  Object.entries(weights).forEach(([planet, w]) => {
    const score = frictionScores[planet] ?? null;
    if (score !== null) {
      weightedSum += score * w;
      totalWeight += w;
    }
  });

  const lfi = totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 0;

  // ── Three data points for bar visualization ──
  // Only average planets that actually HAVE friction data (not ?? 0)
  const residentsWithData = roles.residents.filter(p => frictionScores[p] !== undefined);
  const residentScore: number | null = residentsWithData.length > 0
    ? Math.round(
        residentsWithData.reduce((s, p) => s + frictionScores[p], 0) / residentsWithData.length
      )
    : null;

  const lordScore: number | null =
    roles.lord && frictionScores[roles.lord] !== undefined
      ? frictionScores[roles.lord]
      : null;

  const aspectorsWithData = roles.aspectors.filter(p => frictionScores[p] !== undefined);
  const aspectorScore: number | null = aspectorsWithData.length > 0
    ? Math.round(
        aspectorsWithData.reduce((s, p) => s + frictionScores[p], 0) / aspectorsWithData.length
      )
    : null;

  // Connected planets = everyone with a non-zero weight who has data
  const connectedPlanets = Object.keys(weights)
    .filter(p => frictionScores[p] !== undefined)
    .map(p => p as PlanetName);

  // Per-planet scores for tooltip display
  const planetScores: Record<string, number> = {};
  connectedPlanets.forEach(p => { planetScores[p] = frictionScores[p]; });

  return {
    lfi, residentScore, lordScore, aspectorScore, connectedPlanets,
    residentPlanets: roles.residents,
    lordPlanet: roles.lord,
    aspectorPlanets: roles.aspectors,
    planetScores,
  };
}

/**
 * Compute LFI for all 12 houses at once.
 * Returns a map of house -> HouseLFIResult.
 */
export function computeAllHouseLFIs(
  planetPlacements: Record<string, { house: number }>,
  ascendantSignIndex: number,
  frictionScores: Record<string, number>,
): Record<number, HouseLFIResult> {
  const result: Record<number, HouseLFIResult> = {};
  for (let h = 1; h <= 12; h++) {
    const roles = getHousePlanetRoles(h, planetPlacements, ascendantSignIndex);
    result[h] = computeHouseLFI(roles, frictionScores);
  }
  return result;
}

// ─── Planet narratives ───────────────────────────────────────────────────────

export type FrictionLevel = 'Low' | 'Moderate' | 'High';

export function getFrictionLevel(score: number): FrictionLevel {
  if (score <= 33) return 'Low';
  if (score <= 60) return 'Moderate';
  return 'High';
}

export const FRICTION_LEVEL_COLORS: Record<FrictionLevel, string> = {
  Low:      '#22c55e',
  Moderate: '#f59e0b',
  High:     '#ef4444',
};

export const FRICTION_LEVEL_BG: Record<FrictionLevel, string> = {
  Low:      '#f0fdf4',
  Moderate: '#fffbeb',
  High:     '#fef2f2',
};

const PLANET_NARRATIVES: Record<PlanetName, Record<FrictionLevel, string>> = {
  Sun: {
    Low:      'Solar energy flows with relative ease. Recognition and authority relationships are generally supportive.',
    Moderate: 'Recurring patterns around recognition, authority, or vitality are active. Conscious remediation brings steady improvement.',
    High:     'Significant solar friction — battles for recognition, authority clashes, or energy depletion are a central karmic theme requiring attention.',
  },
  Moon: {
    Low:      'Emotional stability is relatively strong. Inner peace and nurturing relationships come more naturally.',
    Moderate: 'Emotional volatility, anxiety, or mother-related patterns create recurring friction. An active area for healing work.',
    High:     'Deep emotional turbulence or significant mother-karma. Sleep, peace, and emotional grounding need primary and sustained attention.',
  },
  Mars: {
    Low:      'Physical energy and drive are channelled constructively. Conflicts are infrequent and manageable.',
    Moderate: 'Recurring conflict, impatience, or injury patterns are active. Managing reactivity is the key leverage point.',
    High:     'Substantial friction around assertion — anger, disputes, or physical accidents are a defining karmic pattern requiring active remediation.',
  },
  Mercury: {
    Low:      'Communication flows clearly. Decision-making and sibling relationships are relatively uncomplicated.',
    Moderate: 'Miscommunication, overthinking, or sibling tensions create regular friction. Mental-nervous patterns are active.',
    High:     'Significant stress in communication and cognition — persistent miscommunication, indecision, or mental anxiety is a primary karmic load.',
  },
  Jupiter: {
    Low:      'Wisdom and expansion operate with relative ease. Teachers, institutions, and financial dealings are generally supportive.',
    Moderate: 'Debt patterns, over-commitment, or damaged institutional trust are active. Generosity boundaries need strengthening.',
    High:     'Heavy expansion friction — financial over-extension, exploited generosity, or crises of faith are a central karmic theme.',
  },
  Venus: {
    Low:      'Relationship harmony and creative expression flow with relative ease. Partnerships tend to be balancing.',
    Moderate: 'Relationship disappointments, creative blocks, or comfort-seeking patterns are active and recurring.',
    High:     'Significant relational friction — primary relationships, creative life, or partnership patterns carry heavy karmic load.',
  },
  Saturn: {
    Low:      'Delays are minimal. Discipline, structure, and chronic health are relatively stable.',
    Moderate: 'Delayed results, discipline gaps, or persistent low-grade health issues create ongoing friction. Patience is the primary remedy.',
    High:     'Deep karmic pressure — persistent delays, isolation, chronic illness, or systemic obstruction are the primary patterns to address.',
  },
  Rahu: {
    Low:      'Obsessive cycles and confusion are limited. The path forward is relatively clear despite some unconventionality.',
    Moderate: 'Restless desire, past deception, or erratic career patterns are active. Grounding practices make a significant difference.',
    High:     'Intense desire-confusion friction — compulsive chasing, deception patterns, or life-disrupting confusion is a dominant karmic theme.',
  },
  Ketu: {
    Low:      'Detachment is conscious and voluntary. Losses, when they occur, carry learnable lessons rather than prolonged pain.',
    Moderate: 'Disconnection, unexpected losses, or past patterns resurfacing create recurring friction in key life areas.',
    High:     'Significant release-karma — patterns of sudden loss, isolation, or chronic inability to sustain effort are central themes requiring healing.',
  },
};

export function getPlanetNarrative(planet: PlanetName, score: number): string {
  const level = getFrictionLevel(score);
  return PLANET_NARRATIVES[planet][level];
}

// Reconciliation state (for admin view)
export type ReconciliationState = 'CONFIRMED' | 'SUPPRESSED' | 'AMPLIFIED';

/**
 * Compare natal planet strength (0-100, high = strong) against friction (0-100, high = friction).
 * natalStrength high + friction low → AMPLIFIED (natal strength overcomes karma)
 * natalStrength low  + friction high → CONFIRMED  (natal weakness felt as friction)
 * natalStrength high + friction high → SUPPRESSED (strength not translating to ease)
 * Threshold: ±20 gap within expectation = CONFIRMED
 */
export function getReconciliationState(natalStrength: number, frictionScore: number): ReconciliationState {
  // "Expected friction" = inverse of natal strength (strong planet = lower expected friction)
  const expectedFriction = 100 - natalStrength;
  const gap = frictionScore - expectedFriction;

  if (Math.abs(gap) <= 20) return 'CONFIRMED';
  if (gap > 20) return 'SUPPRESSED';   // more friction than natal strength predicts
  return 'AMPLIFIED';                   // less friction than natal weakness predicts
}

export const RECONCILIATION_COLORS: Record<ReconciliationState, { bg: string; text: string; border: string }> = {
  CONFIRMED:  { bg: '#f0fdf4', text: '#15803d', border: '#86efac' },
  SUPPRESSED: { bg: '#fef2f2', text: '#b91c1c', border: '#fca5a5' },
  AMPLIFIED:  { bg: '#eff6ff', text: '#1d4ed8', border: '#93c5fd' },
};
